import React, { useEffect, useState } from "react";
import { Button, Card, Col, Nav, ProgressBar, Row, Table } from "react-bootstrap";
import { Link } from "react-router-dom";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";
import ReactApexChart from "react-apexcharts";
import axiosClient from "../api/axiosClient";
import PageLoader from "../components/PageLoader";

export default function Dashboard() {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ documents: 0, categories: 0, sources: 0, downloads: 0 });
  const [recentDocs, setRecentDocs] = useState([]);
  const [topDocs, setTopDocs] = useState([]);
  const [categoriesDist, setCategoriesDist] = useState([]);
  const [skin, setSkin] = useState(localStorage.getItem("skin-mode") ? "dark" : "");

  useEffect(() => {
    Promise.all([
      axiosClient.get("/stats"),
      axiosClient.get("/documents/recent", { params: { limit: 6 } }),
      axiosClient.get("/documents/popular", { params: { limit: 5 } }),
      axiosClient.get("/categories"),
    ])
      .then(([statsRes, recentRes, popularRes, catRes]) => {
        setStats(statsRes.data.data);
        setRecentDocs(recentRes.data.data || []);
        setTopDocs(popularRes.data.data || []);
        setCategoriesDist(catRes.data.data || []);
      })
      .catch((err) => console.error("Erreur dashboard:", err))
      .finally(() => setLoading(false));
  }, []);

  // ---------- Graphiques ----------

  const downloadsBar = {
    series: [{
      name: "Téléchargements",
      data: topDocs.map((d) => d.downloadCount || 0),
    }],
    options: {
      chart: { type: "bar", toolbar: { show: false }, parentHeightOffset: 0 },
      plotOptions: { bar: { horizontal: true, columnWidth: "55%", borderRadius: 6 } },
      colors: ["#009e60"],
      grid: { borderColor: "rgba(11,29,30,0.07)", padding: { top: -10 } },
      xaxis: {
        categories: topDocs.map((d) => (d.title.length > 28 ? d.title.slice(0, 28) + "…" : d.title)),
        labels: { style: { colors: "#6a7373", fontSize: "11px" } },
      },
      yaxis: { labels: { style: { colors: "#2a3d3e", fontSize: "12px" } } },
      dataLabels: { enabled: true, style: { fontSize: "11px", colors: ["#fff"] } },
      tooltip: { enabled: true },
    },
  };

  const categoryDonut = {
    series: categoriesDist.length
      ? categoriesDist.map(() => Math.round(100 / categoriesDist.length))
      : [100],
    options: {
      chart: { parentHeightOffset: 0 },
      labels: categoriesDist.length ? categoriesDist.map((c) => c.name) : ["Aucune"],
      colors: ["#009e60", "#3a75c4", "#fcd116", "#0b1d1e", "#85b6fe", "#0dcaf0", "#ffc107", "#6c757d"],
      dataLabels: { enabled: false },
      legend: { position: "bottom", fontSize: "12px", labels: { colors: "#2a3d3e" } },
      stroke: { width: 2, colors: ["#fff"] },
      tooltip: { enabled: true },
    },
  };

  const catSpark = {
    series: [{ data: categoriesDist.map((c) => c.id) }],
    options: {
      chart: { sparkline: { enabled: true }, parentHeightOffset: 0 },
      colors: ["#009e60"],
      stroke: { curve: "straight", width: 2 },
      tooltip: { enabled: false },
    },
  };

  const recentSpark = {
    series: [{ data: recentDocs.map((d) => d.publicationYear || 0) }],
    options: {
      chart: { sparkline: { enabled: true }, parentHeightOffset: 0 },
      colors: ["#3a75c4"],
      stroke: { curve: "straight", width: 2 },
      tooltip: { enabled: false },
    },
  };

  if (loading) {
  return (
    <React.Fragment>
      <Header onSkin={setSkin} />
      <div className="main main-app cere-main p-3 p-lg-4">
        <PageLoader message="Chargement du tableau de bord…" />
      </div>
      <Footer />
    </React.Fragment>
  );
}

  return (
    <React.Fragment>
      <Header onSkin={setSkin} />
      <div className="main main-app  cere-main p-3 p-lg-4">

        {/* En-tête */}
        <div className="cere-page-head d-md-flex align-items-center justify-content-between mb-4">
          <div>
            <nav aria-label="breadcrumb">
              <ol className="breadcrumb cere-breadcrumb fs-sm mb-1">
                <li className="breadcrumb-item"><Link to="/dashboard">Admin</Link></li>
                <li className="breadcrumb-item active">Tableau de bord</li>
              </ol>
            </nav>
            <h4 className="cere-page-title mb-0">Bienvenue sur le back-office CERE</h4>
          </div>
          <div className="d-flex gap-2 mt-3 mt-md-0">
            <Button as={Link} to="/documents" className="cere-btn cere-btn-outline">
              <i className="ri-file-list-3-line me-2"></i>
              Documents
            </Button>
            <Button as={Link} to="/documents/new" className="cere-btn cere-btn-primary">
              <i className="ri-add-line me-2"></i>
              Ajouter un document
            </Button>
          </div>
        </div>

        {/* 4 cartes KPI */}
        <Row className="g-3 mb-4">
          <Col sm="6" xl="3">
            <Card className="cere-card cere-kpi-card">
              <Card.Body>
                <div className="cere-kpi-icon cere-kpi-icon--green">
                  <i className="ri-file-text-line"></i>
                </div>
                <div>
                  <div className="cere-kpi-value">{stats.documents}</div>
                  <div className="cere-kpi-label">Documents</div>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col sm="6" xl="3">
            <Card className="cere-card cere-kpi-card">
              <Card.Body>
                <div className="cere-kpi-icon cere-kpi-icon--blue">
                  <i className="ri-price-tag-3-line"></i>
                </div>
                <div>
                  <div className="cere-kpi-value">{stats.categories}</div>
                  <div className="cere-kpi-label">Catégories</div>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col sm="6" xl="3">
            <Card className="cere-card cere-kpi-card">
              <Card.Body>
                <div className="cere-kpi-icon cere-kpi-icon--yellow">
                  <i className="ri-building-line"></i>
                </div>
                <div>
                  <div className="cere-kpi-value">{stats.sources}</div>
                  <div className="cere-kpi-label">Sources</div>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col sm="6" xl="3">
            <Card className="cere-card cere-kpi-card">
              <Card.Body>
                <div className="cere-kpi-icon cere-kpi-icon--ink">
                  <i className="ri-download-line"></i>
                </div>
                <div>
                  <div className="cere-kpi-value">{stats.downloads.toLocaleString("fr-FR")}</div>
                  <div className="cere-kpi-label">Téléchargements</div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* 2 grandes cartes : documents populaires + catégories */}
        <Row className="g-3 mb-4">
          <Col lg="7">
            <Card className="cere-card h-100">
              <Card.Header className="cere-card-header">
                <Card.Title className="cere-card-title">
                  <i className="ri-fire-line me-2"></i>
                  Documents les plus téléchargés
                </Card.Title>
                <Link to="/documents" className="cere-card-link">
                  Tout voir <i className="ri-arrow-right-line"></i>
                </Link>
              </Card.Header>
              <Card.Body>
                {topDocs.length > 0 ? (
                  <ReactApexChart
                    series={downloadsBar.series}
                    options={downloadsBar.options}
                    type="bar"
                    height={320}
                  />
                ) : (
                  <p className="text-muted text-center py-5">Aucun document disponible.</p>
                )}
              </Card.Body>
            </Card>
          </Col>

          <Col lg="5">
            <Card className="cere-card h-100">
              <Card.Header className="cere-card-header">
                <Card.Title className="cere-card-title">
                  <i className="ri-pie-chart-line me-2"></i>
                  Répartition par catégorie
                </Card.Title>
                <Link to="/categories" className="cere-card-link">
                  Tout voir <i className="ri-arrow-right-line"></i>
                </Link>
              </Card.Header>
              <Card.Body className="d-flex justify-content-center align-items-center">
                {categoriesDist.length > 0 ? (
                  <ReactApexChart
                    series={categoryDonut.series}
                    options={categoryDonut.options}
                    type="donut"
                    height={300}
                  />
                ) : (
                  <p className="text-muted text-center py-5">Aucune catégorie.</p>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Derniers ajouts */}
        <Card className="cere-card mb-4">
          <Card.Header className="cere-card-header">
            <Card.Title className="cere-card-title">
              <i className="ri-time-line me-2"></i>
              Derniers documents ajoutés
            </Card.Title>
            <Link to="/documents" className="cere-card-link">
              Tout voir <i className="ri-arrow-right-line"></i>
            </Link>
          </Card.Header>
          <Card.Body>
            {recentDocs.length > 0 ? (
              <Table responsive className="cere-table mb-0">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Titre</th>
                    <th>Catégorie</th>
                    <th>Source</th>
                    <th>Année</th>
                    <th>Format</th>
                    <th className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {recentDocs.map((doc) => (
                    <tr key={doc.id}>
                      <td>{doc.id}</td>
                      <td className="fw-medium">{doc.title}</td>
                      <td>{doc.category?.name || "—"}</td>
                      <td>{doc.source?.name || "—"}</td>
                      <td>{doc.publicationYear || "—"}</td>
                      <td>
                        <span className="cere-badge cere-badge--green">
                          {(doc.fileType || "—").toUpperCase()}
                        </span>
                      </td>
                      <td className="text-end">
                        <Link to={`/documents/${doc.id}/edit`} className="cere-btn cere-btn-sm cere-btn-outline">
                          Éditer
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            ) : (
              <p className="text-muted text-center py-4">Aucun document récent.</p>
            )}
          </Card.Body>
        </Card>

        {/* Mini-cartes résumé */}
        <Row className="g-3">
          <Col sm="6" xl="3">
            <Card className="cere-card cere-mini-card">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-start">
                  <div>
                    <div className="cere-mini-label">Documents publiés</div>
                    <div className="cere-mini-value">{stats.documents}</div>
                  </div>
                  <i className="ri-file-text-line cere-mini-icon"></i>
                </div>
                <ProgressBar
                  now={Math.min(100, stats.documents * 10)}
                  className="cere-progress mt-3"
                />
              </Card.Body>
            </Card>
          </Col>
          <Col sm="6" xl="3">
            <Card className="cere-card cere-mini-card">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-start">
                  <div>
                    <div className="cere-mini-label">Téléchargements</div>
                    <div className="cere-mini-value">{stats.downloads}</div>
                  </div>
                  <i className="ri-download-line cere-mini-icon"></i>
                </div>
                <ProgressBar
                  now={Math.min(100, stats.downloads * 5)}
                  className="cere-progress cere-progress--blue mt-3"
                />
              </Card.Body>
            </Card>
          </Col>
          <Col sm="6" xl="3">
            <Card className="cere-card cere-mini-card">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-start">
                  <div>
                    <div className="cere-mini-label">Catégories</div>
                    <div className="cere-mini-value">{stats.categories}</div>
                  </div>
                  <i className="ri-price-tag-3-line cere-mini-icon"></i>
                </div>
                <ProgressBar
                  now={Math.min(100, stats.categories * 12)}
                  className="cere-progress cere-progress--yellow mt-3"
                />
              </Card.Body>
            </Card>
          </Col>
          <Col sm="6" xl="3">
            <Card className="cere-card cere-mini-card">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-start">
                  <div>
                    <div className="cere-mini-label">Sources</div>
                    <div className="cere-mini-value">{stats.sources}</div>
                  </div>
                  <i className="ri-building-bank-line cere-mini-icon"></i>
                </div>
                <ProgressBar
                  now={Math.min(100, stats.sources * 15)}
                  className="cere-progress cere-progress--ink mt-3"
                />
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Footer />
      </div>
    </React.Fragment>
  );
}